import { KpiResponseStatus } from '../../../../_generated/graphql'
import {
  KpiDashboardAggregate,
  KpiDashboardMember,
  KpiMemberMetric,
  KpiOrgTargetMetric,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { normalizeSearchText } from '../../../util/search-text'

export const UNSET_ROLE_KEY = '__unset_role__'

export type AchievementFilter = 'all' | 'achieved' | 'unachieved'
export type MetricFilterLogic = 'and' | 'or'
export type ResponseFilter = 'all' | 'notAnswered'

export type CoachingFilter = 'all' | 'included' | 'excluded'
export type SortDirection = 'asc' | 'desc'
export type OperatorSortField = 'name' | 'score' | 'actualRate' | 'targetRate'

export interface OperatorListFilter {
  name: string
  /** null は全役職。空配列は役職を一つも選択していない状態。 */
  roleKeys: readonly string[] | null
  metricStatuses: Readonly<Record<string, AchievementFilter>>
  metricLogic: MetricFilterLogic
  response: ResponseFilter
  coaching: CoachingFilter
  reportToUserId: string | null
}

export interface OperatorListSort {
  field: OperatorSortField
  direction: SortDirection
  itemId: string | null
}

export type MetricGapClass = 'neutral' | 'positive' | 'negative'

export interface OperatorMetricCellViewModel {
  itemId: string
  hasActual: boolean
  actualRate: number | null
  targetRate: number | null
  gap: number | null
  achievement: boolean | null
  countText: string
  actualRateText: string
  individualTargetRateText: string
  orgTargetRateCount: number | null
  targetRateText: string
  gapText: string
  gapClass: MetricGapClass
  achievementText: string
}

export function calculateActualRate(metric: KpiMemberMetric): number | null {
  if (metric.denominator === 0) {
    return null
  }

  return (metric.numerator / metric.denominator) * 100
}

export function calculateGap(metric: KpiMemberMetric): number | null {
  const actualRate = calculateActualRate(metric)
  const targetRate = getTargetRate(metric)
  if (actualRate == null || targetRate == null) {
    return null
  }

  return actualRate - targetRate
}

export function calculateAchievement(metric: KpiMemberMetric): boolean | null {
  const actualRate = calculateActualRate(metric)
  const targetRate = getTargetRate(metric)
  if (actualRate == null || targetRate == null) {
    return null
  }

  return actualRate >= targetRate
}

export function calculateScoreAverage(
  scores: readonly number[],
): number | null {
  if (scores.length === 0) {
    return null
  }

  return scores.reduce((sum, score) => sum + score, 0) / scores.length
}

export function buildMetricCellViewModels(
  metrics: readonly KpiMemberMetric[],
  itemIds: readonly string[],
  orgTargets?: readonly KpiOrgTargetMetric[],
): OperatorMetricCellViewModel[] {
  return itemIds.map((itemId) => {
    const metric = getMetricForItem(metrics, itemId)
    const actualRate = calculateActualRate(metric)
    const individualTargetRate = getTargetRate(metric)
    const orgTarget = orgTargets?.find((target) => target.itemId === itemId)
    let targetRate = individualTargetRate
    if (orgTargets != null) {
      if (orgTarget == null) {
        targetRate = null
      } else {
        if (typeof orgTarget.targetRate === 'undefined') {
          throw new Error(`KPI organization target rate is missing: ${itemId}`)
        }
        targetRate = orgTarget.targetRate
      }
    }
    const gap =
      actualRate == null || targetRate == null ? null : actualRate - targetRate
    const achievement =
      actualRate == null || targetRate == null ? null : actualRate >= targetRate

    return {
      itemId,
      hasActual: metric.denominator !== 0,
      actualRate,
      targetRate,
      gap,
      achievement,
      countText: `${formatCount(metric.numerator)}/${formatCount(
        metric.denominator,
      )}件`,
      actualRateText: formatRate(actualRate),
      targetRateText: formatRate(targetRate),
      individualTargetRateText: formatRate(individualTargetRate),
      orgTargetRateCount:
        orgTargets == null
          ? null
          : orgTarget == null
            ? 0
            : orgTarget.targetRateCount,
      gapText: formatGap(gap),
      gapClass: getGapClass(gap),
      achievementText: formatAchievement(achievement),
    }
  })
}

/** 組織目標の適用月数から、全項目未設定と一部未設定の注記を分ける。 */
export function buildOrgTargetNote(
  cells: readonly OperatorMetricCellViewModel[],
  periodMonths: number,
): string | null {
  if (
    cells.length > 0 &&
    cells.every((cell) => cell.orgTargetRateCount === 0)
  ) {
    return '組織目標が未設定です（設定画面から登録できます）'
  }
  if (
    cells.some(
      (cell) =>
        cell.orgTargetRateCount != null &&
        cell.orgTargetRateCount < periodMonths,
    )
  ) {
    return '組織目標が未設定の月を含みます'
  }
  return null
}

export function formatScore(value: number | null | undefined): string {
  if (typeof value === 'undefined') {
    throw new Error('Assessment score is missing')
  }

  if (value == null) {
    return '—'
  }

  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)
}

export function getMemberRoleKey(member: KpiDashboardMember): string {
  return member.roleTitle == null ? UNSET_ROLE_KEY : member.roleTitle
}

export function getMetricForItem(
  metrics: readonly KpiMemberMetric[],
  itemId: string,
): KpiMemberMetric {
  const metric = metrics.find((candidate) => candidate.itemId === itemId)
  if (metric == null) {
    throw new Error(`KPI metric is missing: ${itemId}`)
  }

  return metric
}

export function filterMembers(
  members: readonly KpiDashboardMember[],
  filter: OperatorListFilter,
): KpiDashboardMember[] {
  const normalizedName = normalizeSearchText(filter.name)
  const metricFilters = Object.entries(filter.metricStatuses).filter(
    ([, status]) => status !== 'all',
  )

  return members.filter((member) => {
    if (
      filter.reportToUserId != null &&
      member.userId !== filter.reportToUserId &&
      member.reportToUserId !== filter.reportToUserId
    ) {
      return false
    }

    if (
      filter.response === 'notAnswered' &&
      member.latestSheetResponseStatus !== KpiResponseStatus.NotAnswered
    ) {
      return false
    }
    if (
      normalizedName !== '' &&
      !normalizeSearchText(member.userName).includes(normalizedName)
    ) {
      return false
    }

    if (
      filter.roleKeys != null &&
      !filter.roleKeys.includes(getMemberRoleKey(member))
    ) {
      return false
    }

    if (filter.coaching === 'included' && member.excludedFromCoaching) {
      return false
    }

    if (filter.coaching === 'excluded' && !member.excludedFromCoaching) {
      return false
    }

    if (metricFilters.length === 0) {
      return true
    }

    const matches = metricFilters.map(([itemId, status]) => {
      const achievement = calculateAchievement(
        getMetricForItem(member.metrics, itemId),
      )
      return status === 'achieved'
        ? achievement === true
        : achievement === false
    })

    return filter.metricLogic === 'and'
      ? matches.every(Boolean)
      : matches.some(Boolean)
  })
}

export function isFilterApplied(filter: OperatorListFilter): boolean {
  return (
    normalizeSearchText(filter.name) !== '' ||
    filter.roleKeys != null ||
    filter.coaching !== 'all' ||
    filter.reportToUserId != null ||
    filter.response !== 'all' ||
    Object.values(filter.metricStatuses).some((status) => status !== 'all')
  )
}

export function sortMembers(
  members: readonly KpiDashboardMember[],
  sort: OperatorListSort,
): KpiDashboardMember[] {
  return [...members].sort((left, right) => {
    if (sort.field === 'name') {
      const comparison = compareNames(left.userName, right.userName)
      return sort.direction === 'asc' ? comparison : -comparison
    }

    const leftValue = getSortValue(left, sort)
    const rightValue = getSortValue(right, sort)
    const valueComparison = compareNullableNumbers(
      leftValue,
      rightValue,
      sort.direction,
    )
    if (valueComparison !== 0) {
      return valueComparison
    }

    return compareNames(left.userName, right.userName)
  })
}

export function aggregateMembers(
  members: readonly KpiDashboardMember[],
  itemIds: readonly string[],
): Pick<KpiDashboardAggregate, 'metrics' | 'averageScore'> {
  const memberScoreAverages = members
    .map((member) => calculateScoreAverage(member.scores))
    .filter((score): score is number => score != null)

  return {
    averageScore:
      memberScoreAverages.length === 0
        ? null
        : memberScoreAverages.reduce((sum, score) => sum + score, 0) /
          memberScoreAverages.length,
    metrics: itemIds.map((itemId) => {
      const metrics = members.map((member) =>
        getMetricForItem(member.metrics, itemId),
      )
      const targetRateSum = metrics.reduce(
        (sum, metric) => sum + metric.targetRateSum,
        0,
      )
      const targetRateCount = metrics.reduce(
        (count, metric) => count + metric.targetRateCount,
        0,
      )

      return {
        itemId,
        numerator: metrics.reduce((sum, metric) => sum + metric.numerator, 0),
        denominator: metrics.reduce(
          (sum, metric) => sum + metric.denominator,
          0,
        ),
        targetRate:
          targetRateCount === 0 ? null : targetRateSum / targetRateCount,
        targetRateSum,
        targetRateCount,
      }
    }),
  }
}

function getSortValue(
  member: KpiDashboardMember,
  sort: OperatorListSort,
): number | null {
  if (sort.field === 'score') {
    return member.scores.length === 0 ? null : member.scores[0]
  }

  if (sort.itemId == null) {
    throw new Error(`itemId is required for ${sort.field} sort`)
  }

  const metric = getMetricForItem(member.metrics, sort.itemId)
  return sort.field === 'actualRate'
    ? calculateActualRate(metric)
    : getTargetRate(metric)
}

function getTargetRate(metric: KpiMemberMetric): number | null {
  if (typeof metric.targetRate === 'undefined') {
    throw new Error(`KPI target rate is missing: ${metric.itemId}`)
  }

  return metric.targetRate
}

function formatRate(value: number | null): string {
  return value == null ? '—' : `${value.toFixed(1)}%`
}

export function formatGap(value: number | null): string {
  if (value == null) {
    return '—'
  }

  const sign = value >= 0 ? '+' : '−'
  return `${sign}${Math.abs(value).toFixed(1)}pt`
}

function formatCount(value: number): string {
  return String(value)
}

function formatAchievement(value: boolean | null): string {
  if (value == null) {
    return '—'
  }

  return value ? '達成' : '未達'
}

export function getGapClass(value: number | null): MetricGapClass {
  if (value == null) {
    return 'neutral'
  }

  return value >= 0 ? 'positive' : 'negative'
}

export function compareNullableNumbers(
  left: number | null,
  right: number | null,
  direction: SortDirection,
): number {
  if (left == null && right == null) {
    return 0
  }
  if (left == null) {
    return 1
  }
  if (right == null) {
    return -1
  }

  return direction === 'asc' ? left - right : right - left
}

function compareNames(left: string, right: string): number {
  return left.localeCompare(right, 'ja')
}

/** 最新シートの記入完了を一覧の表示値へ変換する。 */
export function responseStatusDisplay(
  status: KpiDashboardMember['latestSheetResponseStatus'],
): {
  responseStatusLabel: '未完了' | '完了' | 'シートなし'
  responseStatusClass: 'warning' | 'answered' | 'none'
} {
  switch (status) {
    case undefined:
      throw new Error('KPI sheet response status is missing')
    case KpiResponseStatus.NotAnswered:
      return { responseStatusLabel: '未完了', responseStatusClass: 'warning' }
    case KpiResponseStatus.Answered:
      return {
        responseStatusLabel: '完了',
        responseStatusClass: 'answered',
      }
    case null:
      return { responseStatusLabel: 'シートなし', responseStatusClass: 'none' }
  }
}

/** 集計期間に含まれる月数を両端込みで求める。不正な年月・逆転期間は受け付けない。 */
export function countPeriodMonths(
  periodFrom: string,
  periodTo: string,
): number {
  const monthIndex = (period: string): number => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) {
      throw new Error(`Invalid period: ${period}`)
    }
    return Number(period.slice(0, 4)) * 12 + Number(period.slice(5)) - 1
  }
  const months = monthIndex(periodTo) - monthIndex(periodFrom) + 1
  if (months <= 0) throw new Error('Invalid period range')
  return months
}
